import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search as SearchIcon, MapPin, Stethoscope, Scissors, Activity, User } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import {
  specialties,
  surgeries,
  locations,
  getAllTreatments,
  featuredDoctors,
  doctorSlug,
} from "@/lib/healic-data";
import { doctorProfileSlug } from "@/lib/old-doctor-slugs";

export const Route = createFileRoute("/search")({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q : "",
  }),
  head: () => ({
    meta: [
      { title: "Search — Healic Multispeciality Hospital" },
      {
        name: "description",
        content:
          "Search Healic Hospitals for doctors, specialties, surgeries, treatments and locations.",
      },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Search Healic Multispeciality Hospital" },
      { property: "og:url", content: "https://healic.in/search" },
      { property: "og:type", content: "website" },
      {
        property: "og:description",
        content: "Find doctors, specialties, surgeries, treatments and locations at Healic.",
      },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/search" }],
  }),
  component: SearchPage,
});

type Result = {
  kind: "specialty" | "surgery" | "treatment" | "doctor" | "location";
  title: string;
  subtitle?: string;
  to: string;
  params?: Record<string, string>;
};

function SearchPage() {
  const { q: initialQ } = Route.useSearch();
  const [query, setQuery] = useState(initialQ);

  const allResults = useMemo<Result[]>(() => {
    return [
      ...specialties.map<Result>((s) => ({
        kind: "specialty",
        title: s.name,
        subtitle: s.short,
        to: "/specialties/$slug",
        params: { slug: s.slug },
      })),
      ...surgeries.map<Result>((s) => ({
        kind: "surgery",
        title: s.name,
        subtitle: s.short,
        to: "/surgeries/$slug",
        params: { slug: s.slug },
      })),
      ...getAllTreatments().map<Result>((t) => ({
        kind: "treatment",
        title: t.name,
        subtitle: t.specialtyName,
        to: "/treatments/$slug",
        params: { slug: t.slug },
      })),
      ...featuredDoctors.map<Result>((d) => ({
        kind: "doctor",
        title: d.name,
        subtitle: d.specialty,
        to: "/doctors/$slug",
        params: { slug: doctorProfileSlug(doctorSlug(d.name), d.name) },
      })),
      ...locations.map<Result>((l) => ({
        kind: "location",
        title: `Healic ${l.city}`,
        subtitle: l.address,
        to: "/locations/$slug",
        params: { slug: l.slug },
      })),
    ];
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as Result[];
    return allResults
      .filter((r) =>
        `${r.title} ${r.subtitle ?? ""} ${r.kind}`.toLowerCase().includes(q),
      )
      .slice(0, 100);
  }, [allResults, query]);

  const grouped = useMemo(() => {
    const g: Record<Result["kind"], Result[]> = {
      specialty: [],
      surgery: [],
      treatment: [],
      doctor: [],
      location: [],
    };
    for (const r of filtered) g[r.kind].push(r);
    return g;
  }, [filtered]);

  const groupMeta: Record<Result["kind"], { label: string; icon: typeof SearchIcon }> = {
    specialty: { label: "Specialties", icon: Stethoscope },
    surgery: { label: "Surgeries", icon: Scissors },
    treatment: { label: "Treatments", icon: Activity },
    doctor: { label: "Doctors", icon: User },
    location: { label: "Locations", icon: MapPin },
  };

  return (
    <PageShell>
      <div className="bg-muted/40">
        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-20">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">Search</p>
            <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">
              Find what you need at Healic
            </h1>
            <p className="mt-4 max-w-2xl opacity-85">
              Search across doctors, specialties, surgeries, treatments and our hospital locations.
            </p>
            <label className="relative mt-8 flex items-center">
              <SearchIcon className="absolute left-4 h-5 w-5 text-muted-foreground" />
              <input
                type="search"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Try ‘piles’, ‘gynaecology’, ‘Dr. Kanika’, ‘Agra’…"
                className="w-full rounded-pill border border-border bg-background py-4 pl-12 pr-4 text-base text-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                aria-label="Search Healic"
              />
            </label>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
          {!query.trim() ? (
            <div className="rounded-3xl border border-dashed border-border bg-card p-12 text-center">
              <h2 className="font-display text-xl font-semibold">Start typing to search</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Try a condition, doctor name, specialty or city.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-card p-12 text-center">
              <h2 className="font-display text-xl font-semibold">No results for “{query}”</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Try a different keyword or browse our{" "}
                <Link to="/specialties" className="text-accent underline">
                  specialties
                </Link>
                .
              </p>
            </div>
          ) : (
            <div className="space-y-10">
              <p className="text-sm text-muted-foreground">
                Found <span className="font-semibold text-foreground">{filtered.length}</span>{" "}
                result{filtered.length === 1 ? "" : "s"} for “{query}”
              </p>
              {(Object.keys(grouped) as Result["kind"][]).map((kind) => {
                const items = grouped[kind];
                if (items.length === 0) return null;
                const Meta = groupMeta[kind];
                const Icon = Meta.icon;
                return (
                  <div key={kind}>
                    <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold">
                      <Icon className="h-5 w-5 text-accent" />
                      {Meta.label}
                      <span className="text-sm font-normal text-muted-foreground">
                        ({items.length})
                      </span>
                    </h2>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((r, i) => (
                        <Link
                          key={`${r.kind}-${i}`}
                          to={r.to}
                          params={r.params as never}
                          className="group rounded-2xl border border-border bg-card p-4 transition hover:border-accent hover:shadow-soft"
                        >
                          <p className="font-semibold leading-tight group-hover:text-accent">
                            {r.title}
                          </p>
                          {r.subtitle && (
                            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                              {r.subtitle}
                            </p>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </PageShell>
  );
}
